import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { Globe, Loader2, Coins, ChevronRight, Star, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { useSettings } from '@/hooks/useSettings';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/glass';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { useDeleteCurrency } from '../hooks';
import { localizedName } from '@/utils/localized';
import type { Currency, CurrencyListResponse } from '../types';
import { CurrencyFormModal } from './CurrencyFormModal';
import { CurrencyDetailModal } from './CurrencyDetailModal';
import { useModal } from '@/components/modal';
import {
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  GlassSelect,
  CreateButton,
  BulkActionBar,
  StatusDot,
  InfiniteScrollSentinel,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const CURRENCIES = [
  { code: 'ILS', symbol: '₪', label: 'Israeli Shekel (₪)' },
  { code: 'USD', symbol: '$', label: 'US Dollar ($)' },
  { code: 'EUR', symbol: '€', label: 'Euro (€)' },
  { code: 'GBP', symbol: '£', label: 'British Pound (£)' },
];

const LANGUAGES = [
  { code: 'he', label: 'עברית (Hebrew)' },
  { code: 'en', label: 'English' },
];

const CURRENCY_PAGE_SIZE = 20;

export function RegionConfigPage() {
  const { t, i18n } = useTranslation();
  const isRTL = useRTL();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useSettings();
  const deleteMutation = useDeleteCurrency();
  const { openConfirm } = useModal();

  const [currency, setCurrency] = useState('ILS');
  const [currencySymbol, setCurrencySymbol] = useState('₪');
  const [defaultLanguage, setDefaultLanguage] = useState('he');

  // Currencies CRUD state
  const [currencySearch, setCurrencySearch] = useState('');
  const [currencyStatusFilter, setCurrencyStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editCurrency, setEditCurrency] = useState<Currency | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);

  const {
    data: currencyData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: currenciesLoading,
    isError: currenciesError,
  } = useInfiniteQuery({
    queryKey: ['currencies', 'infinite', currencySearch, currencyStatusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = { page: pageParam, limit: CURRENCY_PAGE_SIZE };
      if (currencySearch) params.search = currencySearch;
      if (currencyStatusFilter) params.isActive = currencyStatusFilter;
      const { data } = await http.get<CurrencyListResponse>('/currencies', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.currencies.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });
  const allCurrencies = useMemo(() => currencyData?.pages.flatMap((p) => p.currencies) || [], [currencyData]);
  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allCurrencies);
  const totalCurrencyCount = currencyData?.pages[0]?.pagination.total || 0;
  const activeCurrencyCount = useMemo(() => allCurrencies.filter((c) => c.isActive).length, [allCurrencies]);
  const defaultCurrencyRecord = useMemo(() => allCurrencies.find((c) => c.isDefault), [allCurrencies]);

  const handleBulkDeleteCurrencies = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('currencies.bulkDeleteTitle'),
      message: t('currencies.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        const results = await Promise.allSettled(
          Array.from(selectedIds).map((id) => deleteMutation.mutateAsync(id)),
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) toast.error(t('currencies.bulkDeletePartial', { failed }));
        else toast.success(t('currencies.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  // Load from settings when they arrive
  useEffect(() => {
    if (settings) {
      setCurrency(settings.currency || 'ILS');
      setCurrencySymbol(settings.currencySymbol || '₪');
      setDefaultLanguage(settings.defaultLanguage || 'he');
    }
  }, [settings]);

  const saveSettings = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      await http.put('/settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success(t('settings.saved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleCurrencyChange = (code: string) => {
    setCurrency(code);
    const found = CURRENCIES.find((c) => c.code === code);
    if (found) setCurrencySymbol(found.symbol);
  };

  const handleSave = () => {
    saveSettings.mutate({
      currency,
      currencySymbol,
      defaultLanguage,
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="rounded-xl border border-gray-200 p-8 shadow-sm glass-surface">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="space-y-4">
        <div className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-3 shadow-sm glass-surface">
          <Globe className="h-6 w-6 text-slate-800" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">
            {t('adminNav.regionalSettings')}
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            {t('settings.regionConfigDescription')}
          </p>
        </div>
      </motion.div>

      {/* Regional Overrides */}
      <motion.div variants={staggerItem}>
        <GlassCard title={t('settings.overrides')} icon={<Globe className="h-4 w-4" />}>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Currency */}
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                {t('settings.currency')}
              </label>
              <select
                value={currency}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm focus:outline-none"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Language */}
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                {t('settings.defaultLanguage')}
              </label>
              <select
                value={defaultLanguage}
                onChange={(e) => setDefaultLanguage(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm focus:outline-none"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Save */}
      <motion.div variants={staggerItem} className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saveSettings.isPending}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-primary-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveSettings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('common.save')}
        </button>
      </motion.div>

      {/* ── Currencies Management ── */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title={t('currencies.management')}
          icon={<Coins className="h-4 w-4" />}
          headerAction={<CreateButton label={t('currencies.create')} onClick={() => setCreateModalOpen(true)} />}
        >
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard icon={<Coins className="w-6 h-6" />} label={t('currencies.total')} value={totalCurrencyCount} />
            <StatCard icon={<CheckCircle className="w-6 h-6" />} label={t('currencies.active')} value={activeCurrencyCount} />
            {defaultCurrencyRecord && (
              <div
                className="flex items-center gap-3 p-3 rounded-xl glass-surface col-span-2"
                style={{ backgroundColor: ADMIN_COLORS.surface, border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: ADMIN_COLORS.shadow }}
              >
                <Star className="w-6 h-6 flex-shrink-0" style={{ color: '#f59e0b' }} />
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase">{t('currencies.defaultCurrency')}</p>
                  <p className="text-base font-bold text-slate-800">
                    {defaultCurrencyRecord.symbol} {defaultCurrencyRecord.code} — {localizedName(defaultCurrencyRecord, i18n.language)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Search & filter */}
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <SearchInput value={currencySearch} onChange={setCurrencySearch} placeholder={t('currencies.searchPlaceholder')} />
            <button onClick={selectAll} className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors">
              <input type="checkbox" checked={allSelected} onChange={selectAll} className="w-5 h-5 pointer-events-none" readOnly />
              <span className="font-bold text-sm">{t('currencies.selectAll')}</span>
            </button>
            <GlassSelect value={currencyStatusFilter} onChange={(e) => { setCurrencyStatusFilter(e.target.value); triggerHapticFeedback(); }}>
              <option value="">{t('currencies.allStatuses')}</option>
              <option value="true">{t('currencies.active')}</option>
              <option value="false">{t('currencies.inactive')}</option>
            </GlassSelect>
          </div>

          {/* List */}
          <AnimatePresence mode="wait">
            {currenciesLoading ? (
              <motion.div key="skeleton" className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <CardSkeleton /><CardSkeleton /><CardSkeleton />
              </motion.div>
            ) : currenciesError ? (
              <ErrorState message={t('currencies.loadError')} />
            ) : allCurrencies.length === 0 ? (
              <EmptyState icon={<Coins className="w-14 h-14" />} message={t('currencies.noCurrencies')} />
            ) : (
              <motion.div key="content" className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {allCurrencies.map((cur) => {
                  const selected = isSelected(cur.id);
                  return (
                    <motion.div
                      key={cur.id}
                      role="button"
                      tabIndex={0}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => { triggerHapticFeedback(); setSelectedCurrency(cur); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { triggerHapticFeedback(); setSelectedCurrency(cur); } }}
                      className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 rounded-xl text-start cursor-pointer glass-surface"
                      style={{ backgroundColor: ADMIN_COLORS.surface, border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: selected ? ADMIN_COLORS.shadowMd : ADMIN_COLORS.shadow }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 flex items-center justify-center cursor-pointer" style={{ minWidth: '44px', minHeight: '44px' }} onClick={(e) => { e.stopPropagation(); toggle(cur.id); }}>
                          <input type="checkbox" checked={selected} onChange={() => toggle(cur.id)} onClick={(e) => e.stopPropagation()} className="w-5 h-5 pointer-events-none" />
                        </div>
                        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-lg font-bold" style={{ backgroundColor: cur.isActive ? '#ecfdf5' : '#f3f4f6', border: `1px solid ${ADMIN_COLORS.border}` }}>
                          {cur.symbol}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[200px] md:max-w-none">{localizedName(cur, i18n.language)}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: ADMIN_COLORS.surface, border: `1px solid ${ADMIN_COLORS.border}` }}>{cur.code}</span>
                            {cur.isDefault && (
                              <span className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: '#fef3c7', border: `1px solid ${ADMIN_COLORS.border}` }}>
                                <Star className="w-3 h-3 inline-block mr-0.5" />{t('currencies.default')}
                              </span>
                            )}
                            <span className="text-xs text-slate-500">{cur.decimalPlaces} {t('currencies.decimals')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusDot active={cur.isActive} title={cur.isActive ? t('currencies.active') : t('currencies.inactive')} />
                        <ChevronRight className="w-5 h-5 text-slate-500" style={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          <InfiniteScrollSentinel hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} sentinelRef={sentinelRef} />
        </GlassCard>
      </motion.div>

      {/* Currency Modals */}
      <CurrencyFormModal
        isOpen={createModalOpen || !!editCurrency}
        onClose={() => { queryClient.refetchQueries({ queryKey: ['currencies'] }); setCreateModalOpen(false); setEditCurrency(null); }}
        editData={editCurrency}
      />
      {selectedCurrency && (
        <CurrencyDetailModal
          currency={selectedCurrency}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['currencies'] }); setSelectedCurrency(null); }}
          onEdit={(c) => { setSelectedCurrency(null); setEditCurrency(c); }}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar count={count} onDelete={handleBulkDeleteCurrencies} onClear={clear} isDeleting={deleteMutation.isPending} />
    </motion.div>
  );
}
